import React, { useState, useEffect, useMemo } from 'react';
import { Container, Tabs, Tab, Box, Typography, Alert } from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { getProfile } from '../services/authApiService';
import Profile from './Profile';
import OrganizationProfile from './OrganizationProfile';
import OrganizationUsers from './OrganizationUsers';

const AccountSettings = () => {
  const { user } = useAuth();
  const { isAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const userProfile = await getProfile();
        setProfile(userProfile);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const availableTabs = useMemo(() => {
    const tabs = [{ id: 'profile', label: 'Profile' }];

    const isManager = profile?.access_level === 'manager' || profile?.access_level === 'demo';

    if (isAdmin || isManager) {
      tabs.push({ id: 'organization', label: 'Organisation' });
      tabs.push({ id: 'users', label: 'Users' });
    }

    return tabs;
  }, [profile, isAdmin]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!profile) {
    return (
      <Container sx={{ mt: { xs: 8, md: 12 } }}>
        <Alert severity="error">
          Failed to load profile. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 8 }, mb: 4 }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        {availableTabs.map((tab, index) => (
          <Tab key={tab.id} label={tab.label} value={index} />
        ))}
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Profile />
        </Box>
      )}

      {activeTab === 1 && (isAdmin || profile?.access_level === 'manager' || profile?.access_level === 'demo') && (
        <Box>
          <OrganizationProfile />
        </Box>
      )}

      {activeTab === 2 && (isAdmin || profile?.access_level === 'manager' || profile?.access_level === 'demo') && (
        <Box>
          <OrganizationUsers />
        </Box>
      )}
    </Container>
  );
};

export default AccountSettings;
